/* eslint-disable jsx-a11y/role-supports-aria-props */
// See https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/Tab_Role

import React, { Children } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl } from 'react-intl';

import _get from 'lodash/get';
import _omit from 'lodash/omit';
import { intlShape } from 'constants/propTypes';
import { displayName } from 'helpers/core';
import { bem } from 'helpers/class-name';
import Message from 'components/Message';
import './Tabs.scss';

const messages = defineMessages({
  ariaLabel: {
    id: 'tabs.aria-label',
    defaultMessage: 'Tabs',
  },
  newLabel: {
    id: 'tabs.new-label',
    defaultMessage: 'New',
  },
});

const { block, element } = bem('tabs');

function isTabChild(propValue, key) {
  const child = propValue[key];

  // Because `null`, `undefined`, and `false` are valid, non-rendering children
  if (!child) return;

  if (displayName(child) !== 'Tab') {
    return new Error(`\`<Tabs>\` expects only an array of \`<Tab>\`s as children. Validation failed.`);
  }
}

function isTabLabel(node) {
  if (displayName(node) === 'TabLabel') return true;

  if (
    displayName(node) === 'Message' &&
    (displayName(node.props.as) === 'TabLabel' || _get(node, 'props.as.displayName') === 'TabLabel')
  ) {
    return true;
  }

  return false;
}

function hasTabLabelChild(propValue, key) {
  if (propValue.isFaux) return;

  const children = propValue[key];
  const tabNameChildren = Children.toArray(children).filter(isTabLabel);

  if (tabNameChildren.length !== 1) {
    return new Error(`\`<Tab>\` expects to have exactly 1 <TabLabel> child. Validation failed.`);
  }
}

const TabLabel = ({ children = null }) => children;
TabLabel.displayName = 'TabLabel';
TabLabel.propTypes = {
  children: PropTypes.node,
};

const Tab = ({ children }) => children;
Tab.displayName = 'Tab';
Tab.defaultProps = {
  isFaux: false, // used in the Tabs render
  shouldRender: true, // used in the Tabs constructor
  showNewLabel: false,
};
/* eslint-disable react/no-unused-prop-types */
Tab.propTypes = {
  className: PropTypes.string,
  shouldRender: PropTypes.bool,
  isFaux: PropTypes.bool,
  showNewLabel: PropTypes.bool,
  children: hasTabLabelChild,
};
/* eslint-enable react/no-unused-prop-types */

export class TabsUnwrapped extends React.Component {
  static displayName = 'Tabs';

  static propTypes = {
    children: PropTypes.arrayOf(isTabChild),
    className: PropTypes.string,
    intl: intlShape,
    selectedTab: PropTypes.string,
    onTabChange: PropTypes.func,
    renderAllTabs: PropTypes.bool,
    tabListProps: PropTypes.shape({}),
  };

  static defaultProps = {
    onTabChange: () => {},
  };

  constructor(props) {
    super(props);

    const { selectedTab } = props;

    this.state = {
      // this component can be controlled (selectedTab is decided externally) or uncontrolled (decided internally)
      // this is determined on init and can't change.
      isControlled: !!selectedTab,
      selectedTab: null,
    };
  }

  static getDerivedStateFromProps(props, state) {
    const { children } = props;
    const { isControlled } = state;
    const selectedTab = isControlled ? props.selectedTab : state.selectedTab;
    const renderableChildren = Children.toArray(children).reduce(
      (acc, child, idx) => (!child.props.shouldRender ? acc : acc.concat(child.props.name || `${idx}`)),
      []
    );

    return {
      ...state,
      selectedTab: renderableChildren.includes(selectedTab) ? selectedTab : renderableChildren[0],
    };
  }

  componentDidMount() {
    const { onTabChange, selectedTab: selectedTabProps } = this.props;
    const { isControlled, selectedTab: selectedTabState } = this.state;

    if (isControlled && selectedTabProps !== selectedTabState) {
      onTabChange(selectedTabState);
    }
  }

  handleOnTabClick = tabName => {
    const { onTabChange } = this.props;
    const { isControlled } = this.state;

    if (isControlled) {
      onTabChange(tabName);
    } else {
      this.setState({ selectedTab: tabName }, () => onTabChange(tabName));
    }
  };

  render() {
    const {
      children,
      className,
      intl,
      renderAllTabs,
      tabListProps: { className: tabListClassName, ...tabListProps } = {},
      ...restProps
    } = this.props;
    const { selectedTab } = this.state;

    // can't omit props.selectedTab when destructuring because the same key is also being destructured from the state
    const forwardedProps = _omit(restProps, ['selectedTab', 'onTabChange']);

    const tabsArray = Children.toArray(children);
    const tabsOrder = tabsArray.map((tab, idx) => tab.props.name || `${idx}`);
    const tabsByName = tabsArray.reduce((memo1, tab, idx) => {
      const {
        name = `${idx}`,
        shouldRender = true,
        isFaux = false,
        showNewLabel = false,
        children: tabChildren = [],
        className: tabClassName = '',
        ...restTabProps
      } = tab.props;
      const { label, content } = Children.toArray(tabChildren).reduce(
        (memo2, child) => {
          if (isFaux || isTabLabel(child)) {
            memo2.label = child;
          } else {
            memo2.content.push(child);
          }
          return memo2;
        },
        { label: null, content: [] }
      );

      return {
        ...memo1,
        [name]: {
          name,
          shouldRender,
          isFaux,
          showNewLabel,
          label,
          content,
          tabClassName,
          tabProps: restTabProps,
          isSelected: name === selectedTab,
        },
      };
    }, {});

    // a11y
    const tabIndexActiveElement = 0;
    const tabIndexNonActiveElement = -1;

    return (
      <div className={block(className)} {...forwardedProps}>
        <div
          role="tablist"
          aria-label={intl.formatMessage(messages.ariaLabel)}
          className={element('tab-list', tabListClassName)}
          {...tabListProps}>
          {tabsOrder.map((name, idx) => {
            const { shouldRender, isFaux, isSelected, showNewLabel, label, tabClassName } = tabsByName[name];

            if (!shouldRender) return null;

            const listItemClassNames = element('tab-list-item', { isSelected }, label.props.className);

            if (isFaux) {
              return React.cloneElement(label, {
                className: element('faux-tab', listItemClassNames, tabClassName),
              });
            }

            return (
              <button
                key={name}
                role="tab"
                aria-controls={`panel-${idx}`}
                aria-selected={isSelected}
                id={`tab-${idx}`}
                tabIndex={isSelected ? tabIndexActiveElement : tabIndexNonActiveElement}
                className={listItemClassNames}
                onClick={() => this.handleOnTabClick(name)}
                type="button"
                data-cypress={`tabs-list-item-${name}`}>
                {label}
                {showNewLabel && <Message message={messages.newLabel} className={element('new-label')} />}
              </button>
            );
          })}
        </div>
        {tabsOrder.map((name, idx) => {
          const { shouldRender, isFaux, isSelected, content, tabClassName, tabProps } = tabsByName[name];

          if (!shouldRender || isFaux || (!renderAllTabs && !isSelected)) return null;

          return (
            <div
              key={name}
              role="tabpanel"
              id={`panel-${idx}`}
              aria-labelledby={`tab-${idx}`}
              className={element('tab', tabClassName)}
              {...tabProps}
              hidden={!isSelected}
              tabIndex={isSelected ? tabIndexActiveElement : tabIndexNonActiveElement}
              aria-expanded={isSelected}>
              {content}
            </div>
          );
        })}
      </div>
    );
  }
}

export { Tab, TabLabel };
export default injectIntl(TabsUnwrapped);
